import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Linkedin, ArrowDown } from "lucide-react";
import { IMAGES, LINKEDIN_URL, scrollToId, EASE } from "./shared";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-center pt-28 pb-16 lg:pt-24"
      style={{ backgroundColor: "#FAF9F7" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* Text */}
        <motion.div style={{ y: textY }} className="lg:col-span-6 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="h-px w-10" style={{ background: "#F5821F" }} />
            <span className="eyebrow text-xs font-medium" style={{ color: "#8C8C8C" }}>
              DNHDD, India
            </span>
          </motion.div>

          <h1
            className="font-bold leading-[1.02] tracking-tight text-[13vw] sm:text-6xl lg:text-[5.2rem]"
            style={{ color: "#2B2B2B" }}
          >
            <MaskLine delay={0.15}>Understanding DNHDD.</MaskLine>
            <MaskLine delay={0.32}>
              <span style={{ color: "#3B2C82" }}>Building What’s Next.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.62 }}
            className="mt-7 max-w-xl text-base leading-relaxed"
            style={{ color: "#2B2B2B" }}
          >
            Fortim Ventures creates, grows and partners with businesses designed around the unique
            opportunities of the Union Territory of Dadra &amp; Nagar Haveli and Daman &amp; Diu. For
            businesses looking to establish themselves in the region, we serve as a strategic partner
            and advisor — bringing regional expertise, disciplined execution and long-term thinking to
            every venture we help build.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.98 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-contact-btn"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold text-white transition-colors duration-300"
              style={{ backgroundColor: "#3B2C82" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#6B5CA5")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#3B2C82")}
            >
              Get in Touch
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-linkedin-btn"
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold transition-colors duration-300"
              style={{ border: "1px solid #3B2C82", color: "#3B2C82" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F2F1EF")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <Linkedin size={16} />
              Connect on LinkedIn
            </a>
          </motion.div>
        </motion.div>

        {/* Image */}
        <div className="lg:col-span-6 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="relative overflow-hidden rounded-sm"
            style={{ aspectRatio: "4/5" }}
          >
            <motion.img
              style={{ y: imgY }}
              src={IMAGES.hero}
              alt="Aerial view of Diu Fort and lighthouse beside the sea"
              className="kenburns absolute inset-0 w-full h-[118%] object-cover"
            />
            <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 120px rgba(31,26,56,0.35)" }} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 1 }}
            className="absolute -bottom-5 -left-4 sm:left-6 px-5 py-4 rounded-sm"
            style={{ backgroundColor: "#1F1A38" }}
          >
            <p className="text-[11px] eyebrow" style={{ color: "#F5821F" }}>Rooted in</p>
            <p className="text-sm font-semibold text-white mt-1">Dadra &amp; Nagar Haveli<br />and Daman &amp; Diu</p>
          </motion.div>
        </div>
      </div>

      <button
        onClick={() => scrollToId("about")}
        aria-label="Scroll to about"
        className="hidden lg:flex absolute bottom-8 left-6 lg:left-12 z-0 flex-col items-center gap-2"
        style={{ color: "#8C8C8C" }}
      >
        <span className="eyebrow text-[10px]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </button>
    </section>
  );
};
